import React, { useState, useEffect } from "react";
import axios from "axios";
import Table from "../../Table/Table";
import {
  Button,
  CircularProgress,
  Typography,
  Alert,
  Snackbar,
  Box,
  Paper,
  Chip,
  LinearProgress,
  Stack,
} from "@mui/material";
import {
  Description as ReportIcon,
  Archive as ZipIcon,
  CheckCircleOutline as ClosedIcon,
  CloudUpload as UploadIcon,
  FolderZip as ZipFolderIcon,
} from "@mui/icons-material";

const Closed = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [uploading, setUploading] = useState({});

  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const username = sessionStorage.getItem("username") || "Unknown User";

  const headers = [
    { dbParam: "ncr_number", displayName: "NCR No" },
    { dbParam: "total_reject_qty", displayName: "Module Count" },
    { dbParam: "supplier", displayName: "Supplier" },
    { dbParam: "initiator", displayName: "Created By" },
    { dbParam: "downloads", displayName: "Downloads" },
    { dbParam: "upload", displayName: "Upload Closure Docs" },
    { dbParam: "closure_zip", displayName: "Download Closure ZIP" },
    { dbParam: "issue_date", displayName: "Issue Date" },
    { dbParam: "closed_date", displayName: "Closed Date" },
    { dbParam: "closed_by", displayName: "Closed By" },
    { dbParam: "remarks", displayName: "Closure Remarks" },
  ];

  useEffect(() => {
    const fetchClosedNCRs = async () => {
      const selectedProject = localStorage.getItem("selectedProject");
      if (!selectedProject) {
        setError("No project selected.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.post(`${baseURL}/get-closed-ncr`, {
          projectId: selectedProject,
        });

        if (response.data.success && Array.isArray(response.data.ncrs)) {
          const closedNCRs = response.data.ncrs.map((item) => ({
            id: item.id || item.ncr_number,
            ncr_number: item.ncr_number,
            total_reject_qty: item.total_reject_qty || 0,
            supplier: item.supplier || "N/A",
            initiator: item.initiator || "Unknown",
            issue_date: item.issue_date || "N/A",
            closed_date: item.closed_date || "N/A",
            closed_by: item.closed_by || "Unknown",
            remarks: item.closed_remarks || "No remarks provided",
            closure_documents: item.closure_documents || [],
          }));

          setData(closedNCRs);
        } else {
          setError("No closed NCR data received.");
        }
      } catch (err) {
        console.error("Error fetching closed NCRs:", err);
        setError("Failed to load closed NCRs.");
      } finally {
        setLoading(false);
      }
    };

    fetchClosedNCRs();
  }, [baseURL]);

  // Fixed Upload Handler – now detects success correctly
  const handleUpload = async (ncrNumber, event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading((prev) => ({ ...prev, [ncrNumber]: true }));

    const formData = new FormData();
    formData.append("ncr_number", ncrNumber);
    formData.append("uploaded_by", username);
    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }

    try {
      const response = await axios.post(`${baseURL}upload-closure-doc`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Success detection: check message or uploaded_files array
      if (
        response.data &&
        (response.data.message?.includes("uploaded successfully") ||
          (Array.isArray(response.data.uploaded_files) && response.data.uploaded_files.length > 0))
      ) {
        setSnackbar({
          open: true,
          message: response.data.message || `${files.length} document(s) uploaded successfully.`,
          severity: "success",
        });

        // Optional: Refetch data to update closure_documents list
        // Or update locally if you want to avoid refetch
      } else {
        throw new Error("Unexpected response format");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to upload documents.",
        severity: "error",
      });
    } finally {
      setUploading((prev) => ({ ...prev, [ncrNumber]: false }));
      event.target.value = "";
    }
  };

  const downloadReport = async (ncrNumber) => {
    try {
      const response = await axios.post(
        `${baseURL}generate-ncr-report`,
        { ncr_number: ncrNumber },
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${ncrNumber.replace(/\//g, "_")}_Report.docx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to download report.", severity: "error" });
    }
  };

  const downloadZip = async (ncrNumber) => {
    try {
      const response = await axios.post(
        `${baseURL}/ncr-images-zip`,
        { ncr_number: ncrNumber },
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${ncrNumber.replace(/\//g, "_")}_Images.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to download images ZIP.", severity: "error" });
    }
  };

  const downloadClosureZip = async (ncrNumber) => {
    try {
      const response = await axios.post(
        `${baseURL}/download-closure-docs-zip`,
        { ncr_number: ncrNumber },
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${ncrNumber.replace(/\//g, "_")}_Closure_Documents.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: "Closure documents ZIP downloaded.", severity: "success" });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "No closure documents available or download failed.",
        severity: "warning",
      });
    }
  };

  const renderCell = (header, row) => {
    if (header.dbParam === "downloads") {
      return (
        <Stack direction="row" spacing={1} gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ReportIcon />}
            onClick={() => downloadReport(row.ncr_number)}
            sx={{
              fontSize: "11px",
              minWidth: 100,
              color: "#475569",
              borderColor: "#94a3b8",
              "&:hover": { bgcolor: "#424752", color: "white"},
            }}
          >
            Report
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<ZipIcon />}
            onClick={() => downloadZip(row.ncr_number)}
            sx={{
              fontSize: "11px",
              minWidth: 100,
              color: "#475569",
              borderColor: "#94a3b8",
              "&:hover": { bgcolor: "#424752", color: "white"},
            }}
          >
            Images ZIP
          </Button>
        </Stack>
      );
    }

    if (header.dbParam === "upload") {
      return (
        <label>
          <input
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={(e) => handleUpload(row.ncr_number, e)}
            disabled={uploading[row.ncr_number]}
          />
          <Button
            variant="contained"
            size="small"
            component="span"
            startIcon={uploading[row.ncr_number] ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
            sx={{
              fontSize: "11px",
              bgcolor: "#424752",
              "&:hover": { bgcolor: "#4f46e5" },
              minWidth: 140,
            }}
          >
            {uploading[row.ncr_number] ? "Uploading..." : "Upload Docs"}
          </Button>
        </label>
      );
    }

    if (header.dbParam === "closure_zip") {
      return (
        <Button
          variant="contained"
          size="small"
          startIcon={<ZipFolderIcon />}
          onClick={() => downloadClosureZip(row.ncr_number)}
          sx={{
            fontSize: "11px",
            bgcolor: "#424752",
            "&:hover": { bgcolor: "#15803d" },
            minWidth: 160,
          }}
        >
          Closure Docs ZIP
        </Button>
      );
    }

    if (header.dbParam === "remarks") {
      return (
        <Typography
          variant="body2"
          sx={{
            fontSize: "12px",
            color: "#475569",
            whiteSpace: "pre-wrap",
            maxWidth: "320px",
            lineHeight: 1.6,
          }}
        >
          {row.remarks}
        </Typography>
      );
    }

    return null;
  };

  // ... rest of return JSX remains the same (loading, error, empty state, table)
  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <Box
          sx={{
            bgcolor: "#ffffff",
            borderBottom: "1px solid #e2e8f0",
            p: 4,
            background: "linear-gradient(to bottom, #ffffff, #f8fafc)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ClosedIcon sx={{ fontSize: 40, color: "#16a34a" }} />
            <Box>
              <Typography variant="h4" fontWeight="600" color="#1e293b">
                Closed NCR Dashboard
              </Typography>
              <Typography variant="subtitle1" sx={{ mt: 1, color: "#64748b" }}>
                Resolved Non-Conformance Reports
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: 4 }}>
          {loading ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <CircularProgress size={48} thickness={4} sx={{ color: "#64748b" }} />
              <Typography variant="h6" sx={{ mt: 3, color: "#64748b" }}>
                Loading closed NCRs...
              </Typography>
              <LinearProgress sx={{ mt: 4, borderRadius: 2, height: 6 }} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ borderRadius: 2, border: "1px solid #fee2e2" }}>
              {error}
            </Alert>
          ) : data.length === 0 ? (
            <Paper sx={{ p: 8, textAlign: "center", bgcolor: "#ffffff", border: "1px dashed #cbd5e1" }}>
              <Typography variant="h6" color="#475569">
                No closed NCRs
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, color: "#94a3b8" }}>
                No non-conformances have been closed yet for this project.
              </Typography>
            </Paper>
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
                <Typography variant="h6" fontWeight="500" color="#1e293b">
                  {data.length} Closed Non-Conformance Report{data.length === 1 ? "" : "s"}
                </Typography>
                <Chip
                  label="Resolved"
                  sx={{ bgcolor: "#dcfce7", color: "#166534", fontWeight: "500" }}
                />
              </Box>

              <Table
                tableHeaders={headers}
                tableData={data}
                renderCell={renderCell}
              />
            </>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2, boxShadow: 4 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Closed;